import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaCheck, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useVerifyManualPaymentMutation } from "../../store/api/manualPaymentSlice";
import { useTranslation } from "react-i18next";

const ManualPaymentComplete = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);

  const referenceId = queryParams.get("referenceId");
  const resultCode = queryParams.get("resultCode");
  const hash = queryParams.get("hash");
  const orderNumber = queryParams.get("orderNumber");
  const amount = queryParams.get("amount");
  const cardType = queryParams.get("cardType");

  const { user } = useSelector((state) => state.UserReducer);
  const role = user?.role === "super_admin" ? "admin" : user?.role;

  const [status, setStatus] = useState("processing"); // "processing" | "success" | "failed" | "verify_error"
  const [txnInfo, setTxnInfo] = useState(null);
  const [verifyErrorMessage, setVerifyErrorMessage] = useState(null);

  const [verifyPayment] = useVerifyManualPaymentMutation();

  const doVerify = async () => {
    const manualPaymentId = localStorage.getItem("manual_payment_id");
    const txnNumber = localStorage.getItem("manual_payment_txn");

    if (!manualPaymentId) {
      setStatus("failed");
      return;
    }

    setStatus("processing");

    try {
      const res = await verifyPayment({
        manual_payment_id: Number(manualPaymentId),
        reference_id: referenceId,
        result_code: resultCode,
        hash: hash || undefined,
      }).unwrap();

      // Backend gave us a definitive answer — safe to clear and stop retrying.
      localStorage.removeItem("manual_payment_id");
      localStorage.removeItem("manual_payment_txn");

      if (res.data.payment_status === "completed") {
        setStatus("success");
        setTxnInfo({
          transaction_number: txnNumber || res.data.transaction_number,
          amount: res.data.amount_cents,
          currency: res.data.currency,
        });
        toast.success(t("payment_completed_success"));
      } else {
        setStatus("failed");
      }
    } catch (err) {
      const message = err?.data?.message || t("payment_verification_failed");

      // A prior attempt already resolved this — its message tells us the
      // real outcome, so use that instead of looping on retry.
      const alreadyProcessedMatch = message.match(
        /already processed with status:\s*(\w+)/i,
      );
      if (alreadyProcessedMatch) {
        localStorage.removeItem("manual_payment_id");
        localStorage.removeItem("manual_payment_txn");
        if (alreadyProcessedMatch[1].toLowerCase() === "completed") {
          setStatus("success");
          toast.success(t("payment_completed_success"));
        } else {
          setStatus("failed");
        }
        return;
      }

      // The card may already have been charged by Omni — we just don't know
      // yet because this verify call itself errored (network/server issue).
      // Keep the IDs so the same verification can be safely retried instead
      // of pushing the user toward a brand new (possibly duplicate) payment.
      setVerifyErrorMessage(message);
      setStatus("verify_error");
    }
  };

  useEffect(() => {
    doVerify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="payment-complete">
      <div className="p-0">
        {status === "processing" && (
          <div className="payment-success">
            <div className="success-card">
              <div className="success-icon" style={{ background: "#f0f7ff" }}>
                <div className="spinner-border text-primary" role="status" />
              </div>
              <h2>{t("verifyingPayment")}</h2>
              <p>{t("pleaseWaitConfirmTransaction")}</p>
            </div>
          </div>
        )}

        {status === "success" && (
          <div className="payment-success">
            <div className="success-card">
              <div className="success-icon">
                <FaCheck />
              </div>
              <h2>{t("paymentSuccessful")}</h2>
              <p>{t("manualPaymentProcessed")}</p>

              <div className="order-details">
                {txnInfo?.transaction_number && (
                  <div className="detail-row">
                    <span>{t("transactionNo")}</span>

                    <span>{txnInfo.transaction_number}</span>
                  </div>
                )}
                {orderNumber && (
                  <div className="detail-row">
                    <span>{t("orderId")}</span>

                    <span>{orderNumber}</span>
                  </div>
                )}
                {(amount || txnInfo?.amount) && (
                  <div className="detail-row">
                    <span>{t("amountPaid")}</span>

                    <span>
                      ${((amount || txnInfo?.amount) / 100).toFixed(2)}{" "}
                      {txnInfo?.currency || ""}
                    </span>
                  </div>
                )}
                {cardType && (
                  <div className="detail-row">
                    <span>{t("paymentMethod")}</span>

                    <span>{cardType}</span>
                  </div>
                )}
              </div>

              <div className="d-flex gap-2 justify-content-center">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => navigate(`/manual-payment-history`)}
                >
                  {t("viewHistory")}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => navigate(`/receive-payment`)}
                >
                  <button>{t("newPayment")}</button>
                </button>
              </div>
            </div>
          </div>
        )}

        {status === "verify_error" && (
          <div className="payment-success">
            <div className="success-card">
              <div className="success-icon failed-icon">
                <FaTimes />
              </div>
              <h2>Couldn't Confirm Payment</h2>
              <p>
                Your card may already have been charged, but we couldn't
                confirm it with our system yet. Please don't make a new
                payment — tap Retry below.
              </p>
              {verifyErrorMessage && (
                <p className="text-danger">{verifyErrorMessage}</p>
              )}

              <div className="d-flex gap-2 justify-content-center">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={doVerify}
                >
                  Retry
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => navigate(`/manual-payment-history`)}
                >
                  {t("viewHistory")}
                </button>
              </div>
            </div>
          </div>
        )}

        {status === "failed" && (
          <div className="payment-success">
            <div className="success-card">
              <div className="success-icon failed-icon">
                <FaTimes />
              </div>
              <h2>{t("paymentFailed")}</h2>
              <p>{t("paymentFailedDesc")}</p>
              {orderNumber && (
                <div className="order-details">
                  <div className="detail-row">
                    <span>{t("orderId")}</span>
                    <span>{orderNumber}</span>
                  </div>
                </div>
              )}

              <div className="d-flex gap-2 justify-content-center">
                <button
                  type="button"
                  className="btn btn-primary btn-failed"
                  onClick={() => navigate(`/receive-payment`)}
                >
                  {t("tryAgain")}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => navigate(`/dashboard`)}
                >
                  {t("dashboard")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManualPaymentComplete;
